const bitcoin = require('bitcoinjs-lib')
const bitcoinMessage = require('bitcoinjs-message')
const bs58check = require('bs58check')
const asm = require('./asm')
const { UInt64Buf, ReadBigUInt64LE } = require('./buffers')
const { recoverTypedSignature_v4 } = require('eth-sig-util')
const { toChecksumAddress, isValidChecksumAddress } = require('ethereumjs-util')

let networks = {
  mainnet: {
    "messagePrefix": "\u0018Hazama Signed Message:\n",
    "bech32": "haz",
    "bip32": {
      "public": "0x0488b21e",
      "private": "0x0488ade4"
    },
    "H_pubKeyHash": 41,
    "pubKeyHash": 100,
    "wif": 149
  },
  testnet: {
    "messagePrefix": "\u0018Hazama Signed Message:\n",
    "bech32": "thaz",
    "bip32": {
      "public": "0x0488b21e",
      "private": "0x0488ade4"
    },
    "H_pubKeyHash": 41,
    "pubKeyHash": 100,
    "wif": 149
  }
}

function uint8buf(v) {
  let b = Buffer.alloc(1)
  b.writeUInt8(v)
  return b
}

function uint32lebuf(v) {
  let b = Buffer.alloc(4)
  b.writeUInt32LE(v)
  return b
}

function uint64lebuf(v) {
  let b = Buffer.alloc(8)
  b.writeBigUInt64LE(v)
  return b
}

function varlenBuf(b) {
  let l
  if (b.length < 254) {
    l = Buffer.alloc(1)
    l.writeUInt8(b.length)
  } else {
    l = Buffer.alloc(3)
    l.writeUInt8(255)
    l.writeUInt16LE(b.length, 1)
  }

  return Buffer.concat([l, b])
}

function buildEnvelope(fromKp, data, network, compress) {
  if (!network) network = networks.mainnet

  const { address } = bitcoin.payments.p2pkh({ pubkey: fromKp.publicKey, network })
  const nonce = Date.now() & 0x7FFFFFFF
  const timestamp = Date.now()//BigInt(Date.now())
  const signatureBuf = Buffer.concat([
    varlenBuf(bs58check.decode(address)),
    uint32lebuf(nonce),
    UInt64Buf(timestamp),
    varlenBuf(data)
  ])

  let cmpFlag = 0
  if (compress) {
    cmpFlag = 64
    throw new Error('Compression not yet supported')
  }

  const msg = bitcoin.crypto.sha256(signatureBuf).toString('hex')
  const sig = bitcoinMessage.sign(msg, fromKp.privateKey, fromKp.compressed)

  let envelope = Buffer.concat([
    uint8buf((fromKp.compressed?128:0) | 1 | cmpFlag),
    varlenBuf(sig),
    signatureBuf,
  ])

  return envelope
}

async function buildEnvelopeWithWallet(signer, data, network, compress) {
  if (!network) network = networks.mainnet
  if (!window.ethereum && !signer.provider) {
    throw new Error('Cannot found any wallet.')
  }
  let address = await signer.getAddress()
  
  try {
    if( signer.provider?.provider?.isWalletConnect || signer.provider?.provider?.isMagic ) {
      await signer.provider.send("wallet_switchEthereumChain", [{ chainId: "0x1" }])
    }
    else if( window.ethereum ) {
      await window.ethereum.request({ method: 'wallet_switchEthereumChain', params: [{ chainId: "0x1" }] })
    }
  }
  catch(e){}
  
  address = toChecksumAddress(address)
  
  const nonce = Date.now() & 0x7FFFFFFF
  const timestamp = Date.now()//BigInt(Date.now())
  const signatureBuf = Buffer.concat([
    varlenBuf(Buffer.from(address.slice(2), 'hex')),
    uint32lebuf(nonce),
    UInt64Buf(timestamp),
    varlenBuf(data)
  ])
  
  let cmpFlag = 0
  if (compress) {
    cmpFlag = 64
    throw new Error('Compression not yet supported')
  }
  const msg = bitcoin.crypto.sha256(signatureBuf).toString('hex')
  
  const msgParams = JSON.stringify({
    domain: {
      chainId: 1,
      name: 'HAZAMA',
      version: '1',
    },
    message: {
      hash: msg
    },
    primaryType: 'Transaction',
    types: {
      EIP712Domain: [
        { name: 'name', type: 'string' },
        { name: 'version', type: 'string' },
        { name: 'chainId', type: 'uint256' },
      ],
      Transaction: [
        { name: 'hash', type: 'string' }
      ],
    },
  })
  
  let sig
  if( signer.provider?.provider?.isWalletConnect || signer.provider?.provider?.isMagic ) {
    sig = await signer.provider.send("eth_signTypedData_v4", [address, msgParams])
  }
  else if( window.ethereum ) {
    sig = await window.ethereum.request({ method: 'eth_signTypedData_v4', params: [address, msgParams] })
  }
  
  const recoveredAddr = toChecksumAddress(recoverTypedSignature_v4({
    data: JSON.parse(msgParams),
    sig: sig,
  }))
  
  if( recoveredAddr !== address ) {
    throw new Error(`invalid signature`)
  }
  
  let envelope = Buffer.concat([
    uint8buf((false?128:0) | 2 | cmpFlag),
    varlenBuf(Buffer.from(sig.slice(2), 'hex')),
    signatureBuf,
  ])

  return envelope
}

function Cursor(b) {
  let p = 0
  return {
    readUInt8: () => {
      return b.readUInt8(p++)
    },

    readUInt32LE: () => {
      let r = b.readUInt32LE(p)
      p += 4
      return r
    },
    
    readUInt64LE: () => {
      let r
      try {
        r = b.readBigUInt64LE(p)
        if( r > Date.now() ) {
          throw new Error('invalid data')
        }
      }
      catch(e) {
        r = ReadBigUInt64LE(b, p)
      }
      p += 8
      return r
    },
    
    readVarlenBuf: () => {
      let l = b.readUInt8(p++)
      if (l === 255) {
        l = b.readUInt16LE(p)
        p += 2
      }

      let ret = b.slice(p, p + l)
      p += l
      return ret
    },

    remainingSlice: () => {
      return b.slice(p)
    }
  }
}

function openEnvelope(vn) {
  const reader = Cursor(vn)

  const envelopeType = reader.readUInt8()
  const compressed = (envelopeType & 0x80) !== 0
  const version = envelopeType & 0x7F

  if (version === 1) {
    const sig = reader.readVarlenBuf()
    const sigBuf = reader.remainingSlice()

    const address = bs58check.encode(reader.readVarlenBuf())
    const nonce = reader.readUInt32LE()
    const timestamp = reader.readUInt64LE()
    const data = reader.readVarlenBuf()
    const txid = bitcoin.crypto.sha256(sigBuf).toString('hex')

    if (bitcoinMessage.verify(txid, address, sig)) {
      return {from: address, data, nonce, timestamp: Number(timestamp), txid: txid}
    } else {
      throw new Error('invalid signature')
    }
  }
  else if (version === 2) {
    const sig = reader.readVarlenBuf()
    const sigBuf = reader.remainingSlice()
    
    const address = toChecksumAddress('0x' + reader.readVarlenBuf().toString('hex'))
    const nonce = reader.readUInt32LE()
    const timestamp = reader.readUInt64LE()
    const data = reader.readVarlenBuf()
    const txid = bitcoin.crypto.sha256(sigBuf).toString('hex')
    
    const msgParams = JSON.stringify({
      domain: {
        chainId: 1,
        name: 'HAZAMA',
        version: '1',
      },
      message: {
        hash: txid
      },
      primaryType: 'Transaction',
      types: {
        EIP712Domain: [
          { name: 'name', type: 'string' },
          { name: 'version', type: 'string' },
          { name: 'chainId', type: 'uint256' },
        ],
        Transaction: [
          { name: 'hash', type: 'string' }
        ],
      },
    })
    
    const recoveredAddr = toChecksumAddress(recoverTypedSignature_v4({
      data: JSON.parse(msgParams),
      sig: `0x${sig.toString('hex')}`,
    }))
    
    if ( recoveredAddr === address ) {
      return {from: recoveredAddr, data, nonce, timestamp: Number(timestamp), txid: txid}
    } else {
      throw new Error('invalid signature')
    }
  } else {
    throw new Error(`envelope version ${version} not implemented`)
  }
}

function addressVerifier(network) {
  return (addr) => {
    try {
      const {version, hash} = bitcoin.address.fromBase58Check(addr)
  
      if (version === network.pubKeyHash) {
        return hash
      } else {
        throw new Error(`invalid address version (${version}) expected (${network.pubKeyHash})`)
      }
    }
    catch(e){
      if( !isValidChecksumAddress(addr) ) {
        throw new Error(`invalid address format`)
      }
      return true
    }
  }
}

function utils(network) {
  return {
    addressVerifier: addressVerifier(network)
  }
}

function decode(envelope) {
  let msg = openEnvelope(Buffer.from(envelope, 'hex'))
  let lines = asm.unpack(msg.data).filter(x => x.opName !== 'NOOP' && x.opName !== 'END' && x.opName !== 'REQUIRE')

  let calls = []
  let params = []
  let entrypoints = []
  for (let i=0; i < lines.length; i++) {
    let item = lines[i]
    if (item.opName.startsWith('PUSH')) {
      params.push(item.params[0])
    } else if (item.opName === 'ECALL') {
      calls.push({ [item.params[0]]: params })
      params = []
    } else if (item.opName === 'ENTRY') {
      entrypoints.push(item.params[0])
    }
  }

  let ob = {from: msg.from}
  if (calls.length > 0) {
    ob.calls = calls
  }

  if (entrypoints.length > 0) {
    ob.entrypoints = entrypoints
  }

  return ob
}

module.exports = {
  utils, networks, buildEnvelope, openEnvelope, decode, buildEnvelopeWithWallet,
  asm, tilc: require('./tilc')
}
